import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AutoReplyConfig, UpsertAutoReplyConfigParams } from './types'
import { Data as AutoReplyConfigData } from './useAutoReplyConfig'
import { cacheKeys } from './cacheKeys'

type Data = { auto_reply_config: AutoReplyConfig }

type Variables = UpsertAutoReplyConfigParams

interface MutationContext {
  previousData: AutoReplyConfigData | undefined
  queryKey: readonly unknown[]
}

export const upsertAutoReplyConfig = async ({ workspaceId, params }: { workspaceId: number; params: Variables }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/auto_reply_config`, {
    method: 'PUT',
    body: params,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') throw new DOMException('The operation was aborted.', 'AbortError')
    throw new Error(res.data)
  }
  return res.data
}

export interface Options extends Omit<UseMutationOptions<Data, Error, Variables, MutationContext>, 'mutationFn'> {}

export const useUpsertAutoReplyConfig = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables, MutationContext>(
    (params) => upsertAutoReplyConfig({ workspaceId, params }),
    {
      onMutate: async (variables) => {
        const queryKey = cacheKeys.detail(workspaceId)

        // Cancel any in-flight fetches to avoid overwriting the optimistic update
        await queryClient.cancelQueries({ queryKey })

        const previousData = queryClient.getQueryData<AutoReplyConfigData>(queryKey)

        // Optimistically merge the new values into the cached config
        if (previousData?.auto_reply_config) {
          queryClient.setQueryData<AutoReplyConfigData>(queryKey, {
            auto_reply_config: {
              ...previousData.auto_reply_config,
              ...variables,
            },
          })
        }

        return { previousData, queryKey }
      },
      onSettled: (data, error, variables, context) => {
        // Roll back on error
        if (error && context?.previousData !== undefined && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousData)
        }

        // Always invalidate to reconcile with server state
        if (context?.queryKey) {
          queryClient.invalidateQueries({ queryKey: context.queryKey })
        }

        options?.onSettled?.(data, error, variables, context)
      },
      ...options,
    },
  )
}
